import { useState, useEffect } from 'react';
import { X, Star } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;

// Command Deck dark theme tokens (visual only)
const FONT_DISPLAY = "'Poppins', 'Space Grotesk', sans-serif";
const FONT_BODY = "'Work Sans', system-ui, sans-serif";

/**
 * CrewReviewModal - Simplified review modal for crew members to review clients
 * No payment release functionality - just star rating and comment
 */
const CrewReviewModal = ({ isOpen, onClose, pendingReview, onSubmitSuccess }) => {
  const [rating, setRating] = useState(5);
  const [comment, setComment] = useState('');
  const [loading, setLoading] = useState(false);
  const [hoveredStar, setHoveredStar] = useState(0);
  const user = JSON.parse(localStorage.getItem('shootzu_user') || '{}');

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  // iOS-safe body scroll lock
  useEffect(() => {
    if (isOpen) {
      const scrollY = window.scrollY;
      document.body.style.overflow = 'hidden';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = '100%';
    }

    return () => {
      const scrollY = document.body.style.top;
      document.body.style.overflow = '';
      document.body.style.top = '';
      document.body.style.width = '';
      if (scrollY) {
        window.scrollTo(0, parseInt(scrollY || '0') * -1);
      }
    };
  }, [isOpen]);

  const getRatingText = (rating) => {
    switch (rating) {
      case 1: return 'Poor';
      case 2: return 'Fair';
      case 3: return 'Good';
      case 4: return 'Very Good';
      case 5: return 'Excellent';
      default: return '';
    }
  };

  const handleSubmit = async () => {
    if (rating === 0) {
      toast.error('Please select a rating');
      return;
    }

    try {
      setLoading(true);

      const reviewData = {
        job_id: pendingReview.job_id,
        reviewer_id: user.id,
        reviewee_id: pendingReview.reviewee_id,
        rating: rating,
        review_text: comment.trim(),
      };

      const response = await axios.post(`${API}/api/reviews`, reviewData);

      if (response.data.success) {
        toast.success('Thank you! Your review has been submitted.');
        if (onSubmitSuccess) onSubmitSuccess();
        onClose();

        // Reset form
        setRating(5);
        setComment('');
      }
    } catch (error) {
      console.error('Failed to submit review:', error);
      toast.error(error.response?.data?.detail || 'Failed to submit review');
    } finally {
      setLoading(false);
    }
  };

  const handleSkip = () => {
    setRating(5);
    setComment('');
    onClose();
  };

  if (!isOpen || !pendingReview) return null;

  return (
    <div
      className="fixed inset-0 flex items-center justify-center z-50"
      style={{
        background: 'rgba(8,11,20,.66)',
        backdropFilter: 'blur(5px)',
        WebkitBackdropFilter: 'blur(5px)',
        padding: 20,
        overflow: 'hidden',
        touchAction: 'none',
        fontFamily: FONT_BODY,
      }}
    >
      <div
        style={{
          width: '100%',
          maxWidth: 480,
          maxHeight: '90vh',
          overflowY: 'auto',
          background: '#12162A',
          border: '1px solid rgba(255,255,255,.12)',
          borderRadius: 22,
          boxShadow: '0 40px 90px -30px rgba(0,0,0,.85)',
          touchAction: 'pan-y',
        }}
      >
        {/* Header band */}
        <div
          style={{
            position: 'relative',
            padding: '24px 26px',
            background: 'linear-gradient(120deg,#572B7C,#78287C)',
            borderRadius: '22px 22px 0 0',
          }}
        >
          <button
            onClick={handleSkip}
            aria-label="Close"
            style={{
              position: 'absolute',
              top: 16,
              right: 16,
              width: 34,
              height: 34,
              borderRadius: 10,
              background: 'rgba(255,255,255,.12)',
              border: '1px solid rgba(255,255,255,.18)',
              color: '#fff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
            }}
          >
            <X className="w-[17px] h-[17px]" />
          </button>
          <div style={{ fontFamily: FONT_DISPLAY, fontWeight: 800, fontSize: 13, letterSpacing: '.6px', textTransform: 'uppercase', color: 'rgba(255,255,255,.7)' }}>
            Rate Your Experience
          </div>
          <div style={{ fontFamily: FONT_DISPLAY, fontWeight: 800, fontSize: 22, color: '#fff', marginTop: 6, letterSpacing: '-.4px' }}>
            with {pendingReview.reviewee_name}
          </div>
        </div>

        <div className="p-6" style={{ WebkitOverflowScrolling: 'touch' }}>
          {/* Job Info */}
          <div
            style={{
              marginBottom: 24,
              padding: 16,
              background: 'rgba(255,255,255,.04)',
              border: '1px solid rgba(255,255,255,.1)',
              borderRadius: 14,
            }}
          >
            <p style={{ color: 'rgba(255,255,255,.4)', fontSize: 13, margin: 0 }}>Completed Job</p>
            <p style={{ color: '#fff', fontWeight: 600, marginTop: 4, marginBottom: 0 }}>{pendingReview.job_title}</p>
            {pendingReview.job_date && (
              <p style={{ color: 'rgba(255,255,255,.6)', fontSize: 13, marginTop: 4, marginBottom: 0 }}>{formatDate(pendingReview.job_date)}</p>
            )}
          </div>

          {/* Star Rating */}
          <div style={{ marginBottom: 24, textAlign: 'center' }}>
            <p style={{ color: 'rgba(255,255,255,.7)', marginBottom: 16 }}>
              How was your experience working with this client?
            </p>
            <div className="flex justify-center gap-2" style={{ marginBottom: 12 }}>
              {[1, 2, 3, 4, 5].map((star) => (
                <button
                  key={star}
                  type="button"
                  onClick={() => setRating(star)}
                  onMouseEnter={() => setHoveredStar(star)}
                  onMouseLeave={() => setHoveredStar(0)}
                  className="transition-transform hover:scale-110 p-1"
                  style={{ background: 'transparent', border: 'none', cursor: 'pointer' }}
                >
                  <Star
                    className="w-10 h-10 transition-colors"
                    style={{
                      color: star <= (hoveredStar || rating) ? '#FCD34D' : 'rgba(255,255,255,.18)',
                      fill: star <= (hoveredStar || rating) ? '#FCD34D' : 'transparent',
                    }}
                  />
                </button>
              ))}
            </div>
            <p style={{ color: '#FCD34D', fontWeight: 600, fontSize: 14 }}>
              {getRatingText(hoveredStar || rating)}
            </p>
          </div>

          {/* Comment */}
          <div style={{ marginBottom: 24 }}>
            <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: 'rgba(255,255,255,.6)', marginBottom: 8 }}>
              Your Feedback (Optional)
            </label>
            <textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder="Share your experience working with this client..."
              rows={3}
              style={{
                width: '100%',
                resize: 'none',
                padding: '12px 14px',
                border: '1px solid rgba(255,255,255,.14)',
                borderRadius: 12,
                background: 'rgba(255,255,255,.04)',
                color: '#fff',
                fontFamily: FONT_BODY,
                fontSize: 13.5,
                outline: 'none',
                lineHeight: 1.5,
              }}
              onFocus={(e) => { e.target.style.boxShadow = '0 0 0 3px rgba(40,192,208,.12)'; e.target.style.borderColor = 'rgba(40,192,208,.5)'; }}
              onBlur={(e) => { e.target.style.boxShadow = 'none'; e.target.style.borderColor = 'rgba(255,255,255,.14)'; }}
            />
          </div>

          {/* Action Buttons */}
          <div className="space-y-3">
            <button
              onClick={handleSubmit}
              disabled={loading || rating === 0}
              style={{
                width: '100%',
                padding: '14px',
                borderRadius: 12,
                background: '#28C0D0',
                border: 'none',
                color: '#0C1120',
                fontWeight: 700,
                fontSize: 16,
                cursor: loading || rating === 0 ? 'not-allowed' : 'pointer',
                opacity: loading || rating === 0 ? 0.6 : 1,
              }}
            >
              {loading ? 'Submitting...' : 'Submit Review'}
            </button>

            <button
              onClick={handleSkip}
              style={{
                width: '100%',
                color: 'rgba(255,255,255,.5)',
                fontSize: 14,
                padding: '8px',
                background: 'transparent',
                border: 'none',
                cursor: 'pointer',
              }}
            >
              Skip for now
            </button>
          </div>
        </div>
        <p className="text-[8px] text-center py-1 opacity-20 select-none" style={{ color: '#fff' }}>modal:crew-review</p>
      </div>
    </div>
  );
};

export default CrewReviewModal;
